import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../primitives';
import type { DiaryDay, Activity, FilterOption } from './AgendaPage';
import { MoodFace } from './MoodFace';
import { ActivityButton } from './ActivityButton';

// Single month data structure
export interface MonthData {
  month: string;
  diaryDays: DiaryDay[];
  dateFrom: string;
  dateTill: string;
}

export interface CalendarPageProps {
  months: MonthData[];
  onDayClick: (date: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading?: boolean;
  fieldIds?: string[];
  iconBaseUrl?: string;
  // Filter props
  availableFields?: FilterOption[];
  filteredFieldIds?: string[];
  onFilterChange?: (fieldIds: string[]) => void;
}

function getMoodTrend(currentDay: DiaryDay, previousDay: DiaryDay | undefined): {
  icon: string | null;
  color: string | null;
} {
  if (!currentDay.mood || !previousDay?.mood) {
    return { icon: null, color: null };
  }

  const diff = currentDay.mood - previousDay.mood;

  // Only show trend if difference is >= 5
  if (Math.abs(diff) < 5) {
    return { icon: null, color: null };
  }

  if (diff > 0) {
    return { icon: 'bi-arrow-up', color: 'text-success' };
  } else {
    return { icon: 'bi-arrow-down', color: 'text-danger' };
  }
}

function CalendarCell({
  diaryDay,
  previousDay,
  currentMonth,
  onDayClick,
  fieldIds = [],
  iconBaseUrl = '/assets',
}: {
  diaryDay: DiaryDay;
  previousDay: DiaryDay | undefined;
  currentMonth: string;
  onDayClick: (date: string) => void;
  fieldIds?: string[];
  iconBaseUrl?: string;
}) {
  const date = new Date(diaryDay.date);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  const isFuture = date > today;
  const dayMonth = date.toISOString().substring(0, 7);
  const isCurrentMonth = dayMonth === currentMonth;

  const activeActivities = diaryDay.activities.filter(a => a.active);
  const filteredActivities = fieldIds.length > 0
    ? activeActivities.filter(a => fieldIds.includes(a.field.id))
    : activeActivities;

  const isMuted = fieldIds.length > 0 && filteredActivities.length === 0;
  const trend = getMoodTrend(diaryDay, previousDay);

  const displayActivities = filteredActivities.slice(0, 3);
  const hasMoreActivities = filteredActivities.length > 3;

  return (
    <td
      className={cn(
        'diary-days-calendar_cell',
        isMuted && 'diary-days-calendar_cell--muted',
        isToday && 'table-active',
        isFuture && 'diary-days-calendar_cell--inactive'
      )}
      onClick={() => !isFuture && onDayClick(diaryDay.date)}
      style={{ cursor: isFuture ? 'default' : 'pointer' }}
    >
      <div className="justify-content-between align-items-center">
        <div className="diary-days-calendar_cell-status px-xs-0 px-sm-1 px-md-2 px-lg-3 px-xl-4">
          {!isFuture ? (
            <>
              <div className="diary-days-calendar_mood-face">
                <MoodFace mood={diaryDay.mood} className="calendar-mood-face" />
              </div>
              {trend.icon && (
                <div className={cn('diary-days-calendar_mood-trend', trend.color)}>
                  <i className={cn('bi', trend.icon)} />
                </div>
              )}
            </>
          ) : (
            <div className="diary-days-calendar_mood-face">
              <MoodFace mood={null} className="calendar-mood-face" />
            </div>
          )}
        </div>
        <div className="diary-days-calendar_cell-activities">
          {displayActivities.map(activity => (
            <span key={activity.id} className="diary-days-calendar_cell-activity-dot">
              {activity.field.icon && (
                <img
                  src={`${iconBaseUrl}/icons/${activity.field.icon}.svg`}
                  alt={activity.field.name}
                  title={activity.field.name}
                  style={{ width: '20px', height: '20px' }}
                />
              )}
            </span>
          ))}
          {hasMoreActivities && (
            <span className="diary-days-calendar_cell-activity-more">
              +{filteredActivities.length - 3}
            </span>
          )}
        </div>
      </div>
      <div className={cn('diary-days-calendar_month-day', !isCurrentMonth && 'text-muted')}>
        {date.getDate() === 1 && !isCurrentMonth
          ? date.toLocaleDateString('en-US', { month: 'short' })
          : date.getDate().toString().padStart(2, '0')
        }
      </div>
    </td>
  );
}

// Group structure for organizing fields
interface FieldGroup {
  widgetName: string;
  directFields: FilterOption[];
  fieldSets: Map<string, FilterOption[]>;
}

// Activity Filter Component (same as AgendaPage)
function ActivityFilter({
  availableFields,
  selectedFieldIds,
  onFilterChange,
  isLoading,
}: {
  availableFields: FilterOption[];
  selectedFieldIds: string[];
  onFilterChange: (fieldIds: string[]) => void;
  isLoading?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleField = (fieldId: string) => {
    if (selectedFieldIds.includes(fieldId)) {
      onFilterChange(selectedFieldIds.filter(id => id !== fieldId));
    } else {
      onFilterChange([...selectedFieldIds, fieldId]);
    }
  };

  const clearFilters = () => {
    onFilterChange([]);
  };

  // Filter fields by search query
  const filteredFields = React.useMemo(() => {
    if (!searchQuery.trim()) return availableFields;
    const query = searchQuery.toLowerCase();
    return availableFields.filter(field =>
      field.name.toLowerCase().includes(query) ||
      field.widgetName.toLowerCase().includes(query) ||
      (field.fieldSetName && field.fieldSetName.toLowerCase().includes(query))
    );
  }, [availableFields, searchQuery]);

  // Group fields by widget and fieldset
  const groupedFields = React.useMemo(() => {
    const groups = new Map<string, FieldGroup>();

    for (const field of filteredFields) {
      if (!groups.has(field.widgetName)) {
        groups.set(field.widgetName, {
          widgetName: field.widgetName,
          directFields: [],
          fieldSets: new Map(),
        });
      }

      const group = groups.get(field.widgetName)!;

      if (field.fieldSetName) {
        if (!group.fieldSets.has(field.fieldSetName)) {
          group.fieldSets.set(field.fieldSetName, []);
        }
        group.fieldSets.get(field.fieldSetName)!.push(field);
      } else {
        group.directFields.push(field);
      }
    }

    return Array.from(groups.values());
  }, [filteredFields]);

  const renderFieldButton = (field: FilterOption) => {
    const isSelected = selectedFieldIds.includes(field.id);

    return (
      <div
        key={field.id}
        className={cn(
          'agenda-filter-item',
          isSelected && 'agenda-filter-item--selected'
        )}
        style={{
          transform: 'scale(0.75)',
          transformOrigin: 'top left',
          marginRight: '-18px',
          marginBottom: '-22px',
          opacity: isSelected ? 1 : 0.6,
        }}
      >
        <ActivityButton
          id={field.id}
          name={field.name}
          icon={field.icon}
          variant="toggle"
          active={isSelected}
          score={field.score}
          onClick={() => toggleField(field.id)}
        />
      </div>
    );
  };

  return (
    <div className="agenda-filter mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={isLoading}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
          </svg>
          Filter by Activities
          {selectedFieldIds.length > 0 && (
            <span className="badge bg-primary">{selectedFieldIds.length}</span>
          )}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </button>
        {selectedFieldIds.length > 0 && (
          <button
            className="btn btn-link btn-sm text-danger p-0"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="agenda-filter-options p-3 border rounded bg-light">
          {availableFields.length === 0 ? (
            <p className="text-muted mb-0">No activities available for filtering</p>
          ) : (
            <div>
              {/* Search input */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {groupedFields.length === 0 && searchQuery && (
                <p className="text-muted mb-0">No activities match "{searchQuery}"</p>
              )}

              {groupedFields.map((group, groupIndex) => (
                <div key={group.widgetName} className={groupIndex > 0 ? 'mt-3' : ''}>
                  <h6 className="text-muted mb-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
                    {group.widgetName}
                  </h6>

                  {/* Direct fields (not in a fieldset) */}
                  {group.directFields.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 align-items-start mb-2">
                      {group.directFields.map(renderFieldButton)}
                    </div>
                  )}

                  {/* Fieldsets */}
                  {Array.from(group.fieldSets.entries()).map(([fieldSetName, fields]) => (
                    <div key={fieldSetName} className="ms-2 mb-2">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem' }}>
                        {fieldSetName}
                      </small>
                      <div className="d-flex flex-wrap gap-1 align-items-start">
                        {fields.map(renderFieldButton)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Single month calendar component
function CalendarMonth({
  monthData,
  onDayClick,
  fieldIds = [],
  iconBaseUrl = '/assets',
  allDaysByDate,
}: {
  monthData: MonthData;
  onDayClick: (date: string) => void;
  fieldIds?: string[];
  iconBaseUrl?: string;
  allDaysByDate: Map<string, DiaryDay>;
}) {
  const { month, diaryDays } = monthData;

  // Split into weeks (7 days each)
  const weeks: DiaryDay[][] = [];
  for (let i = 0; i < diaryDays.length; i += 7) {
    weeks.push(diaryDays.slice(i, i + 7));
  }

  const monthDate = new Date(`${month}-01`);
  const monthName = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Helper to get previous day for trend calculation
  const getPreviousDay = (currentDate: string): DiaryDay | undefined => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() - 1);
    const prevDateStr = date.toISOString().split('T')[0];
    return allDaysByDate.get(prevDateStr);
  };

  return (
    <div className="calendar-month mb-4">
      <h3 className="calendar-month-header text-center mb-3">{monthName}</h3>
      <table className="table table-bordered diary-days-calendar">
        <thead>
          <tr>
            {weekDays.map(day => (
              <th key={day} className="text-center">{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIndex) => (
            <tr key={weekIndex}>
              {week.map(day => (
                <CalendarCell
                  key={day.id}
                  diaryDay={day}
                  previousDay={getPreviousDay(day.date)}
                  currentMonth={month}
                  onDayClick={onDayClick}
                  fieldIds={fieldIds}
                  iconBaseUrl={iconBaseUrl}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CalendarPage({
  months,
  onDayClick,
  onLoadMore,
  hasMore,
  isLoading = false,
  fieldIds = [],
  iconBaseUrl = '/assets',
  availableFields = [],
  filteredFieldIds = [],
  onFilterChange,
}: CalendarPageProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Create a map for quick lookups across all months
  const allDaysByDate = React.useMemo(() => {
    const map = new Map<string, DiaryDay>();
    for (const monthData of months) {
      for (const day of monthData.diaryDays) {
        map.set(day.date, day);
      }
    }
    return map;
  }, [months]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <div className="container calendar-infinite-scroll">
      {/* Activity Filter */}
      {onFilterChange && (
        <ActivityFilter
          availableFields={availableFields}
          selectedFieldIds={filteredFieldIds}
          onFilterChange={onFilterChange}
          isLoading={isLoading}
        />
      )}

      {/* Filter Active Indicator */}
      {filteredFieldIds.length > 0 && (
        <div className="alert alert-info py-2 mb-3 d-flex align-items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            Showing days with {filteredFieldIds.length} selected {filteredFieldIds.length === 1 ? 'activity' : 'activities'}.
            Filtered activities are shown first.
          </span>
        </div>
      )}

      {/* Render all months */}
      {months.map((monthData) => (
        <CalendarMonth
          key={monthData.month}
          monthData={monthData}
          onDayClick={onDayClick}
          fieldIds={fieldIds.length > 0 ? fieldIds : filteredFieldIds}
          iconBaseUrl={iconBaseUrl}
          allDaysByDate={allDaysByDate}
        />
      ))}

      {/* Loading/Load more sentinel */}
      {hasMore && (
        <div ref={sentinelRef} className="text-center py-4">
          {isLoading ? (
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <p className="text-muted">Scroll to load more months...</p>
          )}
        </div>
      )}

      {!hasMore && months.length > 0 && (
        <div className="text-center py-4">
          <p className="text-muted">No more months</p>
        </div>
      )}

      {months.length === 0 && !isLoading && (
        <div className="text-center py-4">
          <p className="text-muted">No calendar data found</p>
        </div>
      )}
    </div>
  );
}
